<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {ExtendedProject, ExtendedUser, Pagination} from "@/types";
import {Head, router} from "@inertiajs/vue3";
import {ref, watch} from "vue";
import SearchSidebar from "@/Pages/components/SearchSidebar.vue";
import {useNumber} from "@/composables/number";
import UserListItem from "@/Pages/components/UserListItem.vue";
import ProjectListItem from "@/Pages/components/ProjectListItem.vue";

interface Props {
    title: string
    projectsResultsCount: number,
    results: Pagination<ExtendedUser> | Pagination<ExtendedProject>,
    usersResultsCount: number,
    elapsedTime: number,
    type: 'projects' | 'users',
    search: string,
}

defineOptions({layout: BaseLayout})
const props = defineProps<Props>()
const category = ref(props.type)
const page = ref(props.results.current_page)

const { humanFormat } = useNumber()

watch(category, (value) => {
    router.visit(route('search', {
        _query: {
            category: value,
            q: props.search,
        }
    }), {
        preserveState: true,
    })
})

watch(page, (value) => {
    router.visit(route('search', {
        _query: {
            page: value,
            category: category.value,
            q: props.search,
        }
    }), {
        preserveState: true,
    })
})
</script>

<template>
    <Head title="Search results" />
    <v-navigation-drawer
        class="px-2"
        width="320"
        mobile-breakpoint="md"
    >
        <search-sidebar
            v-model="category"
            :users-count="props.usersResultsCount"
            :projects-count="props.projectsResultsCount"
        />
    </v-navigation-drawer>
    <v-row
        class="fill-height"
        :no-gutters="true"
    >
        <v-col>
            <v-row class="mx-5 mt-0">
                <v-col
                    cols="12"
                    lg="10"
                >
                    <h1 class="text-h6 mb-3">
                        {{ humanFormat(props.type === 'projects' ? props.projectsResultsCount : props.usersResultsCount) }} results
                        <span class="text-body-2 text-grey">({{ elapsedTime }} ms)</span>
                    </h1>
                    <v-list v-if="type === 'users'">
                        <user-list-item
                            v-for="result in results.data"
                            :key="result.name"
                            :user="result as ExtendedUser"
                        />
                    </v-list>
                    <v-list v-if="type === 'projects'">
                        <project-list-item
                            v-for="result in results.data"
                            :key="result.name"
                            :project="result as ExtendedProject"
                        />
                    </v-list>
                    <div class="text-center">
                        <v-pagination
                            v-model="page"
                            :length="results.last_page"
                            size="small"
                            active-color="primary"
                            rounded="lg"
                            total-visible="8"
                            density="comfortable"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<style scoped>

</style>
